import React from 'react';
import * as Styled from './style';

type Props = {
  data: any;
  setFormData: (data: any) => void;
  errors: any;

  setErrors: React.Dispatch<React.SetStateAction<any>>;
  handleChange: (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => void;
  handleBlur: (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>

  ) => void;
  handleCheckboxChange: (day: string) => void;
  handleTimeChange: (day: string, field: 'from' | 'to', value: string) => void;
  addLocation: () => void;
  handleNext: () => void;
  handleBack: () => void;
};

export default function StepTwo({
  data,
  setFormData,
  errors,
  handleChange,
  handleBlur,
  handleCheckboxChange,
  handleTimeChange,
  addLocation,
  handleNext,
  handleBack,
}: Props) {
  const days = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  return (
    <>
      <Styled.Title>Basic Firm Info</Styled.Title>

      <Styled.Group>
        <Styled.Grid2>
          <Styled.Label>
            First Name*
            <Styled.Input
              type='text'
              name='firstName'
              value={data.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
            {errors.firstName && (
              <Styled.Error>{errors.firstName}</Styled.Error>
            )}
          </Styled.Label>

          <Styled.Label>
            Last Name*
            <Styled.Input
              type='text'
              name='lastName'
              value={data.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
            {errors.lastName && <Styled.Error>{errors.lastName}</Styled.Error>}
          </Styled.Label>

          <Styled.Label>
            Company Name*
            <Styled.Input
              type='text'
              name='companyName'
              value={data.companyName}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
            {errors.companyName && (
              <Styled.Error>{errors.companyName}</Styled.Error>
            )}
          </Styled.Label>

          <Styled.Label>
            Email Address*
            <Styled.Input
              type='email'
              name='email'
              value={data.email}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
            {errors.email && <Styled.Error>{errors.email}</Styled.Error>}
          </Styled.Label>

          <Styled.Label>
            What email address should the chatbot provide to clients if they ask
            for one?
            <Styled.Input
              type='email'
              name='emailForClients'
              value={data.emailForClients}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {errors.emailForClients && (
              <Styled.Error>{errors.emailForClients}</Styled.Error>
            )}
          </Styled.Label>

          <Styled.Label>
            What phone number should the chatbot provide to clients if they ask
            for one?
            <Styled.Input
              type='tel'
              name='cellPhone'
              value={data.cellPhone}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {errors.cellPhone && (
              <Styled.Error>{errors.cellPhone}</Styled.Error>
            )}
          </Styled.Label>
        </Styled.Grid2>
      </Styled.Group>

      <Styled.Group>
        <Styled.Label>
          What are your normal business hours?
          <Styled.HelperText>
            (Include weekday/weekend variations if applicable)
          </Styled.HelperText>
        </Styled.Label>
        <Styled.HoursGrid>
          {days.map((day) => (
            <Styled.BusinessHourRow key={day}>
              <span>{day}</span>
              <input
                type='checkbox'
                checked={data.businessHours[day].active}
                onChange={() => handleCheckboxChange(day)}
              />
              <input
                type='time'
                value={data.businessHours[day].from}
                onChange={(e) => handleTimeChange(day, 'from', e.target.value)}
                disabled={!data.businessHours[day].active}
              />
              <span className='separator'>-</span>
              <input
                type='time'
                value={data.businessHours[day].to}
                onChange={(e) => handleTimeChange(day, 'to', e.target.value)}
                disabled={!data.businessHours[day].active}
              />
            </Styled.BusinessHourRow>
          ))}
        </Styled.HoursGrid>
      </Styled.Group>

      <Styled.Group>
        <Styled.Label>
          Do you accept walk-ins, appointments, or both?
          <Styled.HelperText>
            (Explain how a client should engage with your firm)
          </Styled.HelperText>
        </Styled.Label>
        <Styled.Textarea
          name='engagementMethod'
          value={data.engagementMethod}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {errors.engagementMethod && (
          <Styled.Error>{errors.engagementMethod}</Styled.Error>
        )}
      </Styled.Group>

      <Styled.Group>
        <Styled.Label>
          Describe your consultation process.
          <Styled.HelperText>
            (Include in-person/virtual, preferred platforms (Zoom, GoogleMeet,
            etc), requirements, or scheduling steps)
          </Styled.HelperText>
        </Styled.Label>
        <Styled.Textarea
          name='consultationProcess'
          value={data.consultationProcess}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {errors.consultationProcess && (
          <Styled.Error>{errors.consultationProcess}</Styled.Error>
        )}
      </Styled.Group>

      <Styled.Group>
        <Styled.Label>
          Do you have multiple office locations? If so, please list each one and
          provide any relevant details about them.
          <Styled.HelperText>
            (Include addresses, services offered at each, or unique contact info
            if applicable)
          </Styled.HelperText>
        </Styled.Label>
        {data.officeLocations.map((location: string, index: number) => (
          <Styled.SubGroup key={index}>
            <Styled.Label>Location {index + 1}</Styled.Label>
            <Styled.Textarea
              value={location}
              onChange={(e) => {
                const newLocations = [...data.officeLocations];
                newLocations[index] = e.target.value;
                setFormData((prev: any) => ({
                  ...prev,
                  officeLocations: newLocations,
                }));
              }}
              placeholder={`Location name\nServices offered\nContact info\nAddress`}
            />
          </Styled.SubGroup>
        ))}
        <Styled.AddButton type='button' onClick={addLocation}>
          + Add another location
        </Styled.AddButton>
        {errors.officeLocations && (
          <Styled.Error>{errors.officeLocations[0]}</Styled.Error>
        )}
      </Styled.Group>

      <Styled.ButtonGroup>
        <Styled.Button type='button' onClick={handleBack}>
          Go back
        </Styled.Button>
        <Styled.Button type='button' onClick={handleNext}>
          Next step: attorneys & staff →
        </Styled.Button>
      </Styled.ButtonGroup>
    </>
  );
}
